/**
 * The app bar's company slot — ONE position that always answers "which company?".
 *
 * On a collection surface it is the global scope filter; on a company-scoped
 * record it is that record's own company. Which arm renders is decided here, so
 * the two can never both appear and the slot can never silently vanish between
 * a list and the record it links to.
 *
 * This deliberately supersedes the earlier contract in
 * docs/platform/multi-company-design.md § UX, which hid the app-bar control on
 * single-record surfaces and put the record's company in a chip beside the page
 * title instead. That put the answer in two different places depending on the
 * page, which is exactly the "which company am I in?" cost the section was
 * written to avoid. The design doc has been updated alongside this component.
 *
 * Mobile keeps its own arrangement: this slot is `hidden sm:flex` in the header
 * (ui-navigation.md § "Mobile header composition" fixes the 375px icon cluster
 * and a company chip is not in it), and the layout renders the identity inline
 * above the page content instead. Exactly one company control at every width.
 */
import { PageCompanyIndicator } from './PageCompanyIndicator';
import { CompanyScopeSwitcher } from './CompanyScopeSwitcher';
import { usePageCompany } from '../../contexts/PageCompanyContext';

export interface AppBarCompanyControlProps {
  className?: string;
}

export function AppBarCompanyControl({ className }: AppBarCompanyControlProps) {
  const pageCompany = usePageCompany();

  // A published page company wins: we are on a record, and the record's own
  // identity is the honest answer there. The scope filter stays hidden but keeps
  // its value, so returning to the list restores the filter (the navigation
  // invariant in multi-company-design.md § UX).
  if (pageCompany) return <PageCompanyIndicator className={className} />;

  return <CompanyScopeSwitcher className={className} />;
}
